// src/adminFlow/AdminOrdersPage.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API_BASE from '../apiBase';
import { Home, Search, ArrowUpDown, Eye } from 'lucide-react';
import useAutoRefresh from './useAutoRefresh';
import { useListenInvalidation } from './adminInvalidation';

function fmt(n, c='CAD') {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(Number(n||0)); }
  catch { return `$${Number(n||0).toFixed(2)}`; }
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

export default function AdminOrdersPage() {
  const nav = useNavigate();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');          // exact, optional
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [sort, setSort] = useState('date');         // id | total | date | status | email
  const [dir, setDir] = useState('desc');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  const reqId = useRef(0);

  const load = useCallback(async (_offset = offset) => {
    const myId = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: q.trim(),
        status,
        date_from: dateFrom,
        date_to: dateTo,
        sort, dir,
        limit: String(limit),
        offset: String(_offset),
      });
      const r = await fetch(`${API_BASE}/api/admin/orders?` + params.toString(), {
        credentials: 'include',
        cache: 'no-store'
      });
      const j = await r.json();
      if (reqId.current !== myId) return;
      if (r.ok) {
        setRows(j.orders || []);
        setTotal(Number(j.total || 0));
        setLimit(Number(j.limit || 50));
        setOffset(Number(j.offset || 0));
      } else {
        setRows([]); setTotal(0);
      }
    } finally {
      if (reqId.current === myId) setLoading(false);
    }
  }, [q, status, dateFrom, dateTo, sort, dir, limit, offset]);

  // initial + whenever filters change (debounce)
  useEffect(() => {
    const t = setTimeout(() => load(0), 250);
    return () => clearTimeout(t);
  }, [load]);

  // realtime: 30s + focus + cross-tab
  useAutoRefresh(load, { intervalMs: 30000, refetchOnFocus: true });
  useListenInvalidation(() => load());

  function toggleSort(col) {
    if (sort === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(col); setDir(col === 'date' ? 'desc' : 'asc'); }
  }

  const headerBtn = (label, col) => (
    <button
      onClick={() => toggleSort(col)}
      className={`inline-flex items-center gap-1 ${sort === col ? 'text-orange-700' : 'text-gray-600'} hover:text-orange-700`}
      title={`Sort by ${label}`}
    >
      {label}<ArrowUpDown className="w-4 h-4" />
    </button>
  );

  const pageInfo = useMemo(() => {
    const from = total === 0 ? 0 : offset + 1;
    const to = Math.min(offset + rows.length, total);
    return `${from}-${to} of ${total}`;
  }, [offset, rows.length, total]);

  function nextPage() {
    if (offset + limit < total) load(offset + limit);
  }
  function prevPage() {
    if (offset - limit >= 0) load(offset - limit);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>

          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>

            {/* Status filter (optional exact) */}
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="px-2 py-2 rounded-lg border border-gray-300 bg-white"
              title="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="Paid">Paid</option>
              <option value="Processing">Processing</option>
              <option value="Shipped">Shipped</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Refunded">Refunded</option>
            </select>

            {/* Date range */}
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="px-2 py-2 rounded-lg border border-gray-300 bg-white" />
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="px-2 py-2 rounded-lg border border-gray-300 bg-white" />

            {/* Search by email or order # */}
            <form onSubmit={(e)=>{e.preventDefault(); load(0);}} className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={q} onChange={(e)=>setQ(e.target.value)}
                placeholder="Search email or order ID…"
                className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent w-72"
              />
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-sm text-gray-600">{loading ? 'Loading…' : pageInfo}</div>
            <div className="flex items-center gap-2">
              <button onClick={prevPage} disabled={offset===0} className="px-3 py-1 rounded border disabled:opacity-50">Prev</button>
              <button onClick={nextPage} disabled={offset+limit>=total} className="px-3 py-1 rounded border disabled:opacity-50">Next</button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-3 px-4">{headerBtn('Order #', 'id')}</th>
                  <th className="py-3 px-4">{headerBtn('Customer Email', 'email')}</th>
                  <th className="py-3 px-4">{headerBtn('Total', 'total')}</th>
                  <th className="py-3 px-4">{headerBtn('Status', 'status')}</th>
                  <th className="py-3 px-4">{headerBtn('Date', 'date')}</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="py-6 px-4 text-gray-500" colSpan={6}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td className="py-6 px-4 text-gray-500" colSpan={6}>No orders found.</td></tr>
                ) : (
                  rows.map(o => (
                    <tr key={o.id} className="border-t last:border-b-0">
                      <td className="py-3 px-4 font-medium text-gray-900">#{o.id}</td>
                      <td className="py-3 px-4 text-gray-700">{o.customer_email}</td>
                      <td className="py-3 px-4">{fmt(o.total, o.currency)}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-700 border border-orange-100">
                          {o.status || 'Paid'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{fmtDate(o.order_date)}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={()=>nav(`/admin/orders/${o.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" /> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}
